/**
 * Chatbot Widget Embed Script
 * 
 * Usage:
 * <script 
 *   src="https://your-domain.com/widget.js" 
 *   data-api-key="your-api-key"
 *   data-api-url="https://api.your-domain.com/api/v1"
 * ></script>
 */

interface WidgetConfig {
  apiKey: string;
  apiUrl: string;
  wsUrl?: string;
  language?: 'en' | 'sw';
  theme?: 'light' | 'dark' | 'auto';
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  title?: string;
  subtitle?: string;
  placeholder?: string;
  welcomeMessage?: string;
  userAuthenticated?: boolean;
  userId?: string;
  userToken?: string;
}

interface ChatbotWidget {
  init: (config: WidgetConfig) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  setLanguage: (language: 'en' | 'sw') => void;
  destroy: () => void;
}

(function () {
  // Prevent multiple initializations
  if ((window as unknown as { ChatbotWidget?: ChatbotWidget }).ChatbotWidget) {
    console.warn('Chatbot widget already initialized');
    return;
  }

  // Get the current script element
  const currentScript = document.currentScript as HTMLScriptElement | null;

  // Extract configuration from data attributes
  const getConfigFromScript = (): Partial<WidgetConfig> => {
    if (!currentScript) return {};

    return {
      apiKey: currentScript.dataset.apiKey,
      apiUrl: currentScript.dataset.apiUrl,
      wsUrl: currentScript.dataset.wsUrl,
      language: currentScript.dataset.language as 'en' | 'sw',
      theme: currentScript.dataset.theme as 'light' | 'dark' | 'auto',
      position: currentScript.dataset.position as 'bottom-right' | 'bottom-left',
      primaryColor: currentScript.dataset.primaryColor,
      title: currentScript.dataset.title,
      subtitle: currentScript.dataset.subtitle,
      placeholder: currentScript.dataset.placeholder,
      welcomeMessage: currentScript.dataset.welcomeMessage,
      userAuthenticated: currentScript.dataset.userAuthenticated === 'true',
      userId: currentScript.dataset.userId,
      userToken: currentScript.dataset.userToken,
    };
  };

  // Widget container
  let container: HTMLDivElement | null = null;
  let iframe: HTMLIFrameElement | null = null;
  let isInitialized = false;

  // Get widget base URL
  const getWidgetBaseUrl = (): string => {
    if (currentScript?.src) {
      const scriptUrl = new URL(currentScript.src);
      return `${scriptUrl.origin}`;
    }
    return window.location.origin;
  };

  // Build iframe URL with config
  const buildIframeUrl = (config: WidgetConfig): string => {
    const baseUrl = getWidgetBaseUrl();
    const params = new URLSearchParams();

    Object.entries(config).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    });

    return `${baseUrl}/?${params.toString()}`;
  };

  // Create widget container and iframe
  const createWidget = (config: WidgetConfig): void => {
    // Create container
    container = document.createElement('div');
    container.id = 'chatbot-widget-container';
    container.style.cssText = `
      position: fixed;
      bottom: 0;
      ${config.position === 'bottom-left' ? 'left: 0' : 'right: 0'};
      z-index: 2147483647;
      width: 100%;
      height: 100%;
      pointer-events: none;
    `;

    // Create iframe
    iframe = document.createElement('iframe');
    iframe.id = 'chatbot-widget-iframe';
    iframe.src = buildIframeUrl(config);
    iframe.style.cssText = `
      position: absolute;
      bottom: 0;
      ${config.position === 'bottom-left' ? 'left: 0' : 'right: 0'};
      width: 100%;
      max-width: 450px;
      height: 700px;
      max-height: 100vh;
      border: none;
      background: transparent;
      pointer-events: auto;
    `;
    iframe.allow = 'microphone';
    iframe.setAttribute('loading', 'lazy');

    container.appendChild(iframe);
    document.body.appendChild(container);

    // Set up message listener for iframe communication
    window.addEventListener('message', handleIframeMessage);
  };

  // Handle messages from iframe
  const handleIframeMessage = (event: MessageEvent): void => {
    // Validate origin
    const baseUrl = getWidgetBaseUrl();
    if (event.origin !== new URL(baseUrl).origin) {
      return;
    }

    const { type, data } = event.data || {};

    switch (type) {
      case 'chatbot:resize':
        if (iframe && data?.height) {
          iframe.style.height = `${data.height}px`;
        }
        break;
      case 'chatbot:maximized':
        if (iframe && container) {
          if (data?.isMaximized) {
            iframe.style.maxWidth = '100%';
            iframe.style.width = '100%';
            iframe.style.height = '100%';
            iframe.style.maxHeight = '100vh';
            container.style.pointerEvents = 'auto';
          } else {
            iframe.style.maxWidth = '450px';
            iframe.style.width = '100%';
            iframe.style.height = '700px';
            iframe.style.maxHeight = '100vh';
            container.style.pointerEvents = 'none';
          }
        }
        break;
      case 'chatbot:close':
        close();
        break;
      case 'chatbot:ready':
        console.log('Chatbot widget ready');
        break;
    }
  };

  // Send message to iframe
  const sendToIframe = (type: string, data?: unknown): void => {
    if (iframe?.contentWindow) {
      const baseUrl = getWidgetBaseUrl();
      iframe.contentWindow.postMessage({ type, data }, baseUrl);
    }
  };

  // Public API
  const init = (config: WidgetConfig): void => {
    if (isInitialized) {
      console.warn('Chatbot widget already initialized');
      return;
    }

    if (!config.apiKey || !config.apiUrl) {
      console.error('Chatbot widget: apiKey and apiUrl are required');
      return;
    }

    createWidget(config);
    isInitialized = true;
  };

  const open = (): void => {
    sendToIframe('chatbot:open');
  };

  const close = (): void => {
    sendToIframe('chatbot:close');
  };

  const toggle = (): void => {
    sendToIframe('chatbot:toggle');
  };

  const setLanguage = (language: 'en' | 'sw'): void => {
    sendToIframe('chatbot:setLanguage', { language });
  };

  const destroy = (): void => {
    if (container) {
      container.remove();
      container = null;
      iframe = null;
    }
    window.removeEventListener('message', handleIframeMessage);
    isInitialized = false;
  };

  // Expose public API
  const ChatbotWidget: ChatbotWidget = {
    init,
    open,
    close,
    toggle,
    setLanguage,
    destroy,
  };

  (window as unknown as { ChatbotWidget: ChatbotWidget }).ChatbotWidget = ChatbotWidget;

  // Auto-initialize if config is provided via data attributes
  const scriptConfig = getConfigFromScript();
  if (scriptConfig.apiKey && scriptConfig.apiUrl) {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        init(scriptConfig as WidgetConfig);
      });
    } else {
      init(scriptConfig as WidgetConfig);
    }
  }
})();
